import { createSlice } from "@reduxjs/toolkit";
import { defaultSocialAccountContentFilters, defaultSocialAccountCreatorsFilters, defaultTrackedPostsFilters, SocialAccountState } from "types/social-account";
import { fetchSocialAccountTrackedPostsThunk, fetchSocialAccountThunk, fetchSocialAccountCreatorsThunk, fetchSocialAccountContentThunk } from "./thunk";
import { ERROR } from "types";
import { DEFAULT_CREATOR_COLLECTION_ID, DEFAULT_POST_COLLECTION_ID } from "constants/global";

const initialState: SocialAccountState = {
    loading: false,
    error: null,
    list: {
        loading: false,
        last_fetched_at: 0,
        items: [],
        error: null
    },
    items: {},
    trackedPosts: {
        loading: false,
        error: null,
        applied_filter_id: null,
        items: {},
        filters: defaultTrackedPostsFilters
    },
    creators: {
        loading: false,
        error: null,
        applied_filter_id: null,
        items: {},
        filters: defaultSocialAccountCreatorsFilters
    },
    content: {
        loading: false,
        error: null,
        applied_filter_id: null,
        items: {},
        filters: defaultSocialAccountContentFilters
    }
}

const socialAccountState = createSlice({
    name: 'socialAccountState',
    initialState,
    reducers: {
        postAddedToCampaign(state, action) {
            const { socialAccountId, collaboration, trackedPostId } = action.payload;
            const filteredLists = state.trackedPosts.items[socialAccountId]?.filtered_lists ?? {};
            Object.keys(filteredLists).forEach((filterId) => {
                const list = filteredLists[filterId];
                const updatedItems = list.items.map((trackedPost) => {
                    if (trackedPost.social_post.uuid === trackedPostId) {
                        return {
                            ...trackedPost,
                            social_post: {
                                ...trackedPost.social_post,
                                collaboration: collaboration
                            }
                        }
                    }
                    return trackedPost;
                });
                list.items = updatedItems;
            });
            state.trackedPosts.items[socialAccountId] = {
                ...state.trackedPosts.items[socialAccountId],
                filtered_lists: filteredLists
            };
        },
        setTrackedPostsFilters(state, action) {
            state.trackedPosts.filters = action.payload;
        },
        cleanTrackedPostState(state, action) {
            const { socialAccountId } = action.payload;
            delete state.trackedPosts.items[socialAccountId];
        },
        setCreatorsFilters(state, action) {
            state.creators.filters = action.payload;
        },
        cleanCreatorsState(state, action) {
            const { socialAccountId } = action.payload;
            delete state.creators.items[socialAccountId];
        },
        setContentFilters(state, action) {
            state.content.filters = action.payload;
        },
        cleanContentState(state, action) {
            const { socialAccountId } = action.payload;
            delete state.content.items[socialAccountId];
        },
        cleanContentStateAll(state){
            state.content.items = {};
        },
        cleanCreatorsStateAll(state){
            state.creators.items = {};
        },
        removeBookmarkCollection: (state, { payload: { postId, socialAccountId, collectionId } }) => {
            const filteredLists = state.content.items[socialAccountId]?.filtered_lists;
            if (!filteredLists) return;
            for (const list of Object.values(filteredLists)) {
                for (const post of list.items) {
                    if (post.social_post.uuid === postId) {
                        if (collectionId === DEFAULT_POST_COLLECTION_ID) {
                            post.social_post.is_bookmarked = false;
                            post.social_post.bookmark_collections = [];
                        } else {
                            const collections = post.social_post.bookmark_collections ?? [];

                            post.social_post.bookmark_collections =
                                collections.filter((c: string) => c !== collectionId);
                        }
                        break;
                    }
                }
            }
        },
        addBookmarkCollection: (state, { payload: { postId, socialAccountId, collectionId } }) => {
            const filteredLists = state.content.items[socialAccountId]?.filtered_lists;
            if (!filteredLists) return;
            for (const list of Object.values(filteredLists)) {
                for (const post of list.items) {
                    if (post.social_post.uuid === postId) {
                        if (collectionId === DEFAULT_POST_COLLECTION_ID) {
                            post.social_post.is_bookmarked = true;
                        } else {
                            if (!post.social_post.bookmark_collections.includes(collectionId)) {
                                post.social_post.bookmark_collections = [
                                    ...post.social_post.bookmark_collections,
                                    collectionId
                                ];
                            }
                        }
                        break;
                    }
                }
            }
        },
        removeBookmarkCollectionCreator: (state, { payload: { socialAccountId, collectionId } }) => {
            const items = state.creators.items;
            if (!items) return;
            for (const item of Object.values(items)) {
                const filteredLists = item.filtered_lists;
                if (!filteredLists) continue;
                for (const item of Object.values(items)) {
                    const filteredLists = item.filtered_lists;
                    if (!filteredLists) continue;
                    for (const list of Object.values(filteredLists)) {
                        for (const creator of list.items) {
                            if (creator.social_account.uuid === socialAccountId) {
                                if (collectionId === DEFAULT_CREATOR_COLLECTION_ID) {
                                    creator.social_account.is_bookmarked = false;
                                    creator.social_account.bookmark_collections = [];
                                } else {
                                    const collections = creator.social_account.bookmark_collections ?? [];

                                    creator.social_account.bookmark_collections =
                                        collections.filter((c: string) => c !== collectionId);
                                }
                                break;
                            }
                        }
                    }
                }
            }
        },
        addBookmarkCollectionCreator: (state, { payload: { socialAccountId, collectionId } }) => {
            const items = state.creators.items;
            if (!items) return;
            for (const item of Object.values(items)) {
                const filteredLists = item.filtered_lists;
                if (!filteredLists) continue;
                for (const list of Object.values(filteredLists)) {
                    for (const creator of list.items) {
                        if (creator.social_account.uuid === socialAccountId) {
                            if (collectionId === DEFAULT_CREATOR_COLLECTION_ID) {
                                creator.social_account.is_bookmarked = true;
                            } else {
                                if (!(creator.social_account.bookmark_collections ?? []).includes(collectionId)) {
                                    creator.social_account.bookmark_collections = [
                                        ...(creator.social_account.bookmark_collections ?? []),
                                        collectionId
                                    ];
                                }
                            }
                            break;
                        }
                    }
                }
            }
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchSocialAccountThunk.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchSocialAccountThunk.fulfilled, (state, { payload }) => {
                state.loading = false;
                state.error = null;
                const { socialAccountId, socialAccount, cached } = payload;
                state.items[socialAccountId] = {
                    last_fetched_at: cached ? (state.items[socialAccountId]?.last_fetched_at ?? 0) : Date.now(),
                    account: socialAccount
                }
            })
            .addCase(fetchSocialAccountThunk.rejected, (state) => {
                state.loading = false;
                state.error = "Failed to fetch social account"
            })

            .addCase(fetchSocialAccountTrackedPostsThunk.pending, (state) => {
                state.trackedPosts.loading = true;
                state.trackedPosts.error = null;
            })
            .addCase(fetchSocialAccountTrackedPostsThunk.fulfilled, (state, action) => {
                state.trackedPosts.loading = false;
                state.trackedPosts.error = null;
                const filterId = action.payload.filterId;
                const socialAccountId = action.payload.socialAccountId;
                const existingData = state.trackedPosts.items?.[socialAccountId]?.filtered_lists?.[filterId]?.items ?? [];
                const finalList = action.payload.filters.page === 1 ? action.payload.items : [...existingData, ...action.payload.items];
                state.trackedPosts.items[socialAccountId] = {
                    ...state.trackedPosts.items[socialAccountId],
                    filtered_lists: {
                        ...(state.trackedPosts.items[socialAccountId]?.filtered_lists ?? {}),
                        [filterId]: {
                            items: finalList,
                            page: action.payload.filters.page,
                            expired: false,
                            last_fetched_at: Date.now(),
                            has_more: action.payload.items.length > 0,
                            filters: action.payload.filters,
                            count: finalList.length,
                            error: null,
                            loading: false
                        }
                    },
                    total: finalList.length
                }
                state.trackedPosts.applied_filter_id = filterId;
            })
            .addCase(fetchSocialAccountTrackedPostsThunk.rejected, (state, action) => {
                state.trackedPosts.loading = false;
                if (action.payload !== ERROR.CACHED)
                    state.trackedPosts.error = action.payload || "Failed to fetch applications"
            })
            .addCase(fetchSocialAccountCreatorsThunk.pending, (state) => {
                state.creators.loading = true;
                state.creators.error = null;
            })
            .addCase(fetchSocialAccountCreatorsThunk.fulfilled, (state, action) => {
                state.creators.loading = false;
                state.creators.error = null;
                const filterId = action.payload.filterId;
                const socialAccountId = action.payload.socialAccountId;
                const existingData = state.creators.items?.[socialAccountId]?.filtered_lists?.[filterId]?.items ?? [];
                const finalList = action.payload.filters.page === 1 ? action.payload.items : [...existingData, ...action.payload.items];
                state.creators.items[socialAccountId] = {
                    ...state.creators.items[socialAccountId],
                    filtered_lists: {
                        ...(state.creators.items[socialAccountId]?.filtered_lists ?? {}),
                        [filterId]: {
                            items: finalList,
                            page: action.payload.filters.page,
                            expired: false,
                            last_fetched_at: Date.now(),
                            has_more: action.payload.items.length > 0,
                            filters: action.payload.filters,
                            count: finalList.length,
                            error: null,
                            loading: false
                        }
                    },
                    total: finalList.length
                }
                state.creators.applied_filter_id = filterId;
            })
            .addCase(fetchSocialAccountCreatorsThunk.rejected, (state, action) => {
                state.creators.loading = false;
                if (action.payload !== ERROR.CACHED)
                    state.creators.error = action.payload || "Failed to fetch social account creators"
            })
            .addCase(fetchSocialAccountContentThunk.pending, (state) => {
                state.content.loading = true;
                state.content.error = null;
            })
            .addCase(fetchSocialAccountContentThunk.fulfilled, (state, action) => {
                state.content.loading = false;
                state.content.error = null;
                const filterId = action.payload.filterId;
                const socialAccountId = action.payload.socialAccountId;
                const existingData = state.content.items?.[socialAccountId]?.filtered_lists?.[filterId]?.items ?? [];
                const finalList = action.payload.filters.page === 1 ? action.payload.items : [...existingData, ...action.payload.items];
                state.content.items[socialAccountId] = {
                    ...state.content.items[socialAccountId],
                    filtered_lists: {
                        ...(state.content.items[socialAccountId]?.filtered_lists ?? {}),
                        [filterId]: {
                            items: finalList,
                            page: action.payload.filters.page,
                            expired: false,
                            last_fetched_at: Date.now(),
                            has_more: action.payload.items.length > 0,
                            filters: action.payload.filters,
                            count: finalList.length,
                            error: null,
                            loading: false
                        }
                    },
                    total: finalList.length
                }
                state.content.applied_filter_id = filterId;
            })
            .addCase(fetchSocialAccountContentThunk.rejected, (state, action) => {
                state.content.loading = false;
                if (action.payload !== ERROR.CACHED)
                    state.content.error = action.payload || "Failed to fetch content"
            })
    }
})

export const { postAddedToCampaign, setTrackedPostsFilters, cleanTrackedPostState, setCreatorsFilters, cleanCreatorsState, setContentFilters, cleanContentState, removeBookmarkCollection, addBookmarkCollection, removeBookmarkCollectionCreator, addBookmarkCollectionCreator, cleanContentStateAll, cleanCreatorsStateAll } = socialAccountState.actions;

export default socialAccountState.reducer;